// src/pages/CompareListsPage.jsx
import { useState, useEffect, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useUser } from "@clerk/react";
import { useMyProviders } from "../context/MyProvidersContext";
import {
  getListSummary,
  getListGeneralByYear,
  getListTopProviders,
  getListAllCompanies,
  getListOwnershipSummary,
  getListResearchSummary,
  getListAllOwnershipCompanies,
  getListAllResearchCompanies,
  getListOwnershipDetail,
  getListCompanyTrend,
  getListOwnershipTrend,
  getListResearchTrend,
  formatCurrency,
  DATASET_YEARS,
} from "../utils/cmsApi";

const CHART_COLORS = ["#f59e0b", "#6366f1", "#14b8a6"];
const LINE_STYLES = ["solid", "dashed", "dotted"];
const ALL_YEARS = Object.keys(DATASET_YEARS).map(Number).sort();
const DEFAULT_YEAR = Math.max(...ALL_YEARS);
const YEAR_OPTIONS = ["all", ...ALL_YEARS.slice().reverse()];

const TABS = [
  { id: "general", label: "General" },
  { id: "ownership", label: "Ownership" },
  { id: "research", label: "Research" },
];

function formatCompact(value) {
  if (!value || value === 0) return "—";
  if (value >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(1)}B`;
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
}

function exportCsv(rows, filename) {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const csv = [
    headers.join(","),
    ...rows.map((r) =>
      headers.map((h) => `"${String(r[h] ?? "").replace(/"/g, '""')}"`).join(",")
    ),
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Year dropdown ─────────────────────────────────────────────────────────────
function YearDropdown({ selectedYear, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-white text-sm hover:border-white/30 transition-colors min-w-28"
      >
        <span className="flex-1 text-left">{selectedYear === "all" ? "All Years" : selectedYear}</span>
        <span className="text-white/40">▾</span>
      </button>
      {open && (
        <div className="absolute top-full right-0 mt-1 border border-white/15 rounded-lg shadow-xl z-30 overflow-hidden min-w-28" style={{ background: "#0f172a" }}>
          <div className="max-h-64 overflow-y-auto">
            {YEAR_OPTIONS.map((y) => (
              <button
                key={y}
                onClick={() => { onChange(String(y)); setOpen(false); }}
                className={`w-full text-left px-3 py-2 text-sm transition-colors hover:bg-white/5 ${selectedYear === String(y) ? "text-gold-400 bg-gold-500/5" : "text-white/70"}`}
              >
                {y === "all" ? "All Years" : y}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const ALL_COMPANIES_KEY = "__ALL__";

// ── Company multi-select dropdown ─────────────────────────────────────────────
function CompanyDropdown({ companies, selected, onChange, maxSelect = 3, showAllOption = false }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  function toggle(name) {
    if (name === ALL_COMPANIES_KEY) {
      onChange(selected.includes(ALL_COMPANIES_KEY) ? [] : [ALL_COMPANIES_KEY]);
      return;
    }
    // Deselect "all" if picking individual companies
    const filtered = selected.filter((n) => n !== ALL_COMPANIES_KEY);
    if (filtered.includes(name)) {
      onChange(filtered.filter((n) => n !== name));
    } else if (filtered.length < maxSelect) {
      onChange([...filtered, name]);
    }
  }

  const isAllSelected = selected.includes(ALL_COMPANIES_KEY);
  const label = selected.length === 0
    ? "Select companies…"
    : isAllSelected
    ? "All companies (total)"
    : selected.length === 1
    ? selected[0].length > 22 ? selected[0].slice(0, 22) + "…" : selected[0]
    : `${selected.length} companies selected`;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-white/70 text-xs hover:border-white/30 transition-colors min-w-48"
      >
        <span className="flex-1 text-left">{label}</span>
        <span className="text-white/40">▾</span>
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 border border-white/15 rounded-lg shadow-xl z-30 overflow-hidden min-w-64" style={{ background: "#0f172a" }}>
          <div className="px-3 py-2 border-b border-white/10">
            <p className="text-white/30 text-xs">Select up to {maxSelect} companies</p>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {showAllOption && (
              <button
                onClick={() => toggle(ALL_COMPANIES_KEY)}
                className={`w-full text-left px-3 py-2 text-xs transition-colors flex items-center gap-2 border-b border-white/10 ${
                  isAllSelected ? "text-gold-400 bg-gold-500/5" : "text-white/70 hover:bg-white/5"
                }`}
              >
                <span className={`w-3 h-3 rounded-sm border shrink-0 flex items-center justify-center ${isAllSelected ? "bg-gold-500 border-gold-500" : "border-white/30"}`}>
                  {isAllSelected && <span className="text-black text-xs leading-none">✓</span>}
                </span>
                <span className="truncate">All companies (total)</span>
              </button>
            )}
            {companies.map((c) => {
              const isSelected = selected.includes(c.companyName);
              const isDisabled = !isSelected && !isAllSelected && selected.filter(n => n !== ALL_COMPANIES_KEY).length >= maxSelect;
              return (
                <button
                  key={c.companyName}
                  onClick={() => toggle(c.companyName)}
                  disabled={isDisabled}
                  className={`w-full text-left px-3 py-2 text-xs transition-colors flex items-center gap-2 ${
                    isSelected ? "text-gold-400 bg-gold-500/5" :
                    isDisabled ? "text-white/20 cursor-not-allowed" :
                    "text-white/70 hover:bg-white/5"
                  }`}
                >
                  <span className={`w-3 h-3 rounded-sm border shrink-0 flex items-center justify-center ${isSelected ? "bg-gold-500 border-gold-500" : "border-white/30"}`}>
                    {isSelected && <span className="text-black text-xs leading-none">✓</span>}
                  </span>
                  <span className="truncate">{c.companyName}</span>
                  <span className="ml-auto text-white/30 shrink-0">{formatCompact(c.grandTotal)}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Company trend line chart ───────────────────────────────────────────────────
function CompanyTrendChart({ lists, trendData, selectedCompanies, selectedYear, metric, title }) {
  const [hoveredYear, setHoveredYear] = useState(null);
  const [tooltipX, setTooltipX] = useState(0);
  const svgRef = useRef(null);

  if (!lists.length || !selectedCompanies.length) return null;

  // For ALL_COMPANIES_KEY — aggregate all companies per list per year
  function getByYear(listId, company) {
    if (company === ALL_COMPANIES_KEY) {
      const allData = trendData[listId] || [];
      const yearMap = new Map();
      allData.forEach((c) => {
        (c.byYear || []).forEach((r) => {
          yearMap.set(r.year, (yearMap.get(r.year) || 0) + (r.total || 0));
        });
      });
      return ALL_YEARS.map((yr) => ({ year: yr, total: yearMap.get(yr) || 0 }));
    }
    const companyData = trendData[listId]?.find((c) => c.companyName === company);
    return companyData?.byYear || [];
  }

  const allYearValues = lists.flatMap((l) =>
    selectedCompanies.flatMap((company) => {
      const byYear = getByYear(l.id, company);
      return byYear.map((r) => r.total || 0);
    })
  );
  const maxVal = Math.max(...allYearValues, 1);

  // SVG dimensions
  const W = 700, H = 160, PAD = { top: 20, right: 10, bottom: 24, left: 48 };
  const chartW = W - PAD.left - PAD.right;
  const chartH = H - PAD.top - PAD.bottom;
  const xStep = chartW / (ALL_YEARS.length - 1);

  function xPos(year) {
    const idx = ALL_YEARS.indexOf(year);
    return PAD.left + idx * xStep;
  }
  function yPos(val) {
    return PAD.top + chartH - (val / maxVal) * chartH;
  }

  function buildPath(byYear) {  // eslint-disable-line no-unused-vars
    const points = ALL_YEARS.map((yr) => {
      const row = byYear.find((r) => r.year === yr);
      return { x: xPos(yr), y: yPos(row?.total || 0), val: row?.total || 0 };
    });
    return {
      d: points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" "),
      points,
    };
  }

  // Y axis labels
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((f) => ({
    val: maxVal * f,
    y: PAD.top + chartH - f * chartH,
  }));

  function handleSvgMouseMove(e) {
    if (!svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const svgWidth = rect.width;
    const scaleX = W / svgWidth;
    const mouseX = (e.clientX - rect.left) * scaleX;
    // Find nearest year
    let nearest = ALL_YEARS[0];
    let minDist = Infinity;
    ALL_YEARS.forEach((yr) => {
      const dist = Math.abs(xPos(yr) - mouseX);
      if (dist < minDist) { minDist = dist; nearest = yr; }
    });
    setHoveredYear(nearest);
    setTooltipX((xPos(nearest) / W) * 100); // as % of SVG width
  }

  function handleSvgMouseLeave() {
    setHoveredYear(null);
  }

  function exportTrend() {
    const rows = [];
    lists.forEach((l) => {
      selectedCompanies.forEach((company) => {
        const companyData = trendData[l.id]?.find((c) => c.companyName === company);
        ALL_YEARS.forEach((yr) => {
          const row = companyData?.byYear?.find((r) => r.year === yr);
          rows.push({
            "List": l.list_name,
            "Company": company,
            "Year": yr,
            [metric]: row?.total || 0,
          });
        });
      });
    });
    exportCsv(rows, `company_trend_${metric.toLowerCase().replace(/\s+/g, "_")}.csv`);
  }

  return (
    <div className="card mb-6">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-white/40 text-xs uppercase tracking-widest">{title}</h2>
        <button
          onClick={exportTrend}
          className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-white/15 text-white/50 hover:text-white hover:border-white/40 transition-all"
        >
          ↓ Export CSV
        </button>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-x-4 gap-y-1.5 mb-3">
        {lists.map((l, li) =>
          selectedCompanies.map((company, ci) => (
            <div key={`${l.id}-${company}`} className="flex items-center gap-1.5">
              <svg width="20" height="10">
                <line
                  x1="0" y1="5" x2="20" y2="5"
                  stroke={CHART_COLORS[li]}
                  strokeWidth="2"
                  strokeDasharray={ci === 1 ? "4,2" : ci === 2 ? "2,2" : "none"}
                />
              </svg>
              <span className="text-white/40 text-xs">
                {l.list_name}{company !== ALL_COMPANIES_KEY ? ` · ${company.length > 20 ? company.slice(0, 20) + "…" : company}` : " · All companies"}
              </span>
            </div>
          ))
        )}
      </div>

      {/* SVG chart */}
      <div className="overflow-x-auto relative">
        <svg
          ref={svgRef}
          width="100%"
          viewBox={`0 0 ${W} ${H}`}
          style={{ minWidth: "400px" }}
          onMouseMove={handleSvgMouseMove}
          onMouseLeave={handleSvgMouseLeave}
        >
          {/* Y grid lines and labels */}
          {yTicks.map((t, i) => (
            <g key={i}>
              <line x1={PAD.left} y1={t.y} x2={W - PAD.right} y2={t.y} stroke="rgba(255,255,255,0.05)" strokeWidth="1" />
              <text x={PAD.left - 4} y={t.y + 4} textAnchor="end" fill="rgba(255,255,255,0.25)" fontSize="9">
                {formatCompact(t.val)}
              </text>
            </g>
          ))}

          {/* Hovered year vertical line */}
          {hoveredYear && (
            <line
              x1={xPos(hoveredYear)}
              y1={PAD.top}
              x2={xPos(hoveredYear)}
              y2={PAD.top + chartH}
              stroke="rgba(255,255,255,0.2)"
              strokeWidth="1"
              strokeDasharray="3,2"
            />
          )}

          {/* Selected year highlight band */}
          {selectedYear !== "all" && (() => {
            const x = xPos(parseInt(selectedYear));
            return (
              <rect
                x={x - xStep / 2}
                y={PAD.top}
                width={xStep}
                height={chartH}
                fill="rgba(255,255,255,0.03)"
                rx="2"
              />
            );
          })()}

          {/* Lines */}
          {lists.map((l, li) =>
            selectedCompanies.map((company, ci) => {
              const byYear = getByYear(l.id, company);
              if (!byYear.length) return null;
              const { d, points } = buildPath(byYear);
              const dashArray = ci === 1 ? "6,3" : ci === 2 ? "2,3" : "none";
              return (
                <g key={`${l.id}-${company}`}>
                  <path
                    d={d}
                    fill="none"
                    stroke={CHART_COLORS[li]}
                    strokeWidth="2"
                    strokeDasharray={dashArray}
                    opacity={selectedYear === "all" ? 0.85 : 0.35}
                  />
                  {points.map((p, pi) => {
                    const yr = ALL_YEARS[pi];
                    const isSelected = selectedYear !== "all" && parseInt(selectedYear) === yr;
                    return p.val > 0 ? (
                      <circle
                        key={pi}
                        cx={p.x}
                        cy={p.y}
                        r={isSelected ? 5 : 3}
                        fill={CHART_COLORS[li]}
                        opacity={selectedYear === "all" ? 0.85 : isSelected ? 1 : 0.25}
                      />
                    ) : null;
                  })}
                </g>
              );
            })
          )}

          {/* X axis labels */}
          {ALL_YEARS.map((yr) => {
            const isSelected = selectedYear !== "all" && parseInt(selectedYear) === yr;
            return (
              <text
                key={yr}
                x={xPos(yr)}
                y={H - 4}
                textAnchor="middle"
                fill={isSelected ? "rgba(255,255,255,0.7)" : "rgba(255,255,255,0.25)"}
                fontSize="9"
                fontWeight={isSelected ? "600" : "400"}
              >
                {yr}
              </text>
            );
          })}
        </svg>

        {/* Hover tooltip */}
        {hoveredYear && (() => {
          const rows = [];
          lists.forEach((l, li) => {
            selectedCompanies.forEach((company, ci) => {
              const byYear = getByYear(l.id, company);
              const row = byYear.find((r) => r.year === hoveredYear);
              const val = row?.total || 0;
              const dashLabel = ci === 1 ? "- -" : ci === 2 ? "···" : "—";
              const companyLabel = company === ALL_COMPANIES_KEY
                ? "All companies"
                : company.length > 22 ? company.slice(0, 22) + "…" : company;
              rows.push({ listName: l.list_name, company: companyLabel, val, color: CHART_COLORS[li], dash: dashLabel });
            });
          });
          const hasData = rows.some((r) => r.val > 0);
          // Position tooltip: flip to left side if near right edge
          const flipLeft = tooltipX > 65;
          return (
            <div
              className="absolute pointer-events-none z-20"
              style={{
                top: "20px",
                ...(flipLeft
                  ? { right: `${100 - tooltipX}%`, marginRight: "12px" }
                  : { left: `${tooltipX}%`, marginLeft: "12px" }),
              }}
            >
              <div className="rounded-lg border border-white/15 shadow-xl px-3 py-2.5 text-xs"
                style={{ background: "#0f172a", minWidth: "160px" }}>
                <p className="text-white/50 text-xs font-medium mb-2 border-b border-white/10 pb-1.5">
                  {hoveredYear}
                </p>
                {hasData ? (
                  <div className="space-y-1.5">
                    {rows.map((r, i) => (
                      <div key={i} className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span className="text-xs shrink-0" style={{ color: r.color }}>{r.dash}</span>
                          <span className="text-white/50 truncate text-xs">{r.listName}</span>
                        </div>
                        <span className="text-xs shrink-0 font-mono" style={{ color: r.color }}>
                          {r.val > 0 ? formatCompact(r.val) : "—"}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-white/30 text-xs">No data for {hoveredYear}</p>
                )}
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}

// ── Payment trend bar chart ────────────────────────────────────────────────────
function PaymentTrendChart({ lists, summaries, yearData, selectedYear }) {
  function getPaymentByYear(listId) {
    const summary = summaries[listId];
    if (summary?.paymentByYear) return summary.paymentByYear;
    const fallback = yearData?.[listId];
    if (fallback?.paymentByYear) return fallback.paymentByYear;
    return [];
  }

  const maxVal = Math.max(
    ...lists.flatMap((l) => {
      const byYear = getPaymentByYear(l.id);
      return byYear.length ? byYear.map((r) => r.total || 0) : [0];
    }), 1
  );

  function exportChart() {
    const rows = [];
    ALL_YEARS.forEach((year) => {
      lists.forEach((l) => {
        const byYear = getPaymentByYear(l.id);
        const yearRow = byYear.find((r) => r.year === year);
        rows.push({ "List Name": l.list_name, "Year": year, "Total Payments": yearRow?.total || 0, "Payment Count": yearRow?.cnt || 0 });
      });
    });
    exportCsv(rows, "payment_trend_comparison.csv");
  }

  return (
    <div className="card mb-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-white/40 text-xs uppercase tracking-widest">Payment trend — all years</h2>
        <button onClick={exportChart} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-white/15 text-white/50 hover:text-white hover:border-white/40 transition-all">
          ↓ Export CSV
        </button>
      </div>
      <div className="flex gap-4 mb-3 flex-wrap">
        {lists.map((l, i) => (
          <div key={l.id} className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm" style={{ background: CHART_COLORS[i] }} />
            <span className="text-white/50 text-xs">{l.list_name}</span>
          </div>
        ))}
      </div>
      <div className="relative" style={{ height: "180px" }}>
        <div className="absolute inset-0 flex items-end gap-2 pb-6">
          {ALL_YEARS.map((year) => (
            <div key={year} className="flex-1 flex items-end gap-0.5 h-full">
              {lists.map((l, i) => {
                const byYear = getPaymentByYear(l.id);
                const yearRow = byYear.find((r) => r.year === year);
                const val = yearRow?.total || 0;
                const heightPct = (val / maxVal) * 100;
                const isSelected = selectedYear !== "all" && parseInt(selectedYear) === year;
                return (
                  <div key={l.id} className="flex-1 flex flex-col items-center justify-end h-full relative">
                    {val > 0 && (
                      <div className="absolute w-full text-center pointer-events-none" style={{ bottom: `${Math.max(heightPct, 2)}%`, paddingBottom: "2px" }}>
                        <span style={{ fontSize: "7px", color: CHART_COLORS[i], opacity: isSelected ? 1 : 0.85 }}>
                          {val >= 1_000_000 ? `$${(val / 1_000_000).toFixed(1)}M` : val >= 1_000 ? `$${(val / 1_000).toFixed(0)}K` : `$${val.toFixed(0)}`}
                        </span>
                      </div>
                    )}
                    <div className="w-full rounded-t transition-all" style={{
                      height: val > 0 ? `${Math.max(heightPct, 2)}%` : "2px",
                      background: val > 0 ? CHART_COLORS[i] : "rgba(255,255,255,0.05)",
                      opacity: selectedYear === "all" ? 0.8 : isSelected ? 1 : 0.25,
                    }} />
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        <div className="absolute bottom-0 left-0 right-0 flex gap-1">
          {ALL_YEARS.map((year) => (
            <div key={year} className="flex-1 text-center">
              <span className="text-white/30 transition-colors" style={{ fontSize: "9px", color: selectedYear !== "all" && parseInt(selectedYear) === year ? "rgba(255,255,255,0.7)" : undefined }}>
                {year}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── General tab column ────────────────────────────────────────────────────────
function GeneralColumn({ list, summary, yearData, topProviders, loadingProviders, color, selectedYear, onExportCompanies, onExportProviders }) {
  const isAllYears = selectedYear === "all";
  const activeData = (isAllYears && summary) ? summary : yearData;

  function handleExportSpecialties() {
    const specialties = activeData?.specialtyBreakdown || [];
    const yearLabel = isAllYears ? "all_years" : selectedYear;
    exportCsv(
      specialties.map((s) => {
        const parts = (s.specialty || "").split("|");
        const label = parts[parts.length - 1].trim() || s.specialty;
        return {
          "Specialty (Display)": label,
          "Specialty (Full CMS)": s.specialty,
          "Provider Count": s.cnt,
          "Total Payments": s.total || 0,
          "Year": yearLabel,
          "List": list.list_name,
        };
      }),
      `${list.list_name.replace(/\s+/g, "_")}_specialties_${yearLabel}.csv`
    );
  }

  if (!activeData) {
    return (
      <div className="card flex flex-col gap-4" style={{ borderTop: `3px solid ${color}` }}>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full shrink-0" style={{ background: color }} />
          <span className="font-display text-xl text-white">{list.list_name}</span>
        </div>
        <div className="text-white/30 text-sm animate-pulse">Loading data…</div>
      </div>
    );
  }

  const totalListSize = summary?.providerCount || activeData?.providerCount || 0;
  const providersWithPayments = activeData?.providersWithPayments || 0;
  const totalPayments = activeData?.totalPaymentsAllYears || 0;
  const paymentRate = totalListSize > 0 ? ((providersWithPayments / totalListSize) * 100).toFixed(0) : 0;
  const avgPerProvider = providersWithPayments > 0 ? totalPayments / providersWithPayments : 0;
  const topCompanies = (activeData.topCompanies || []).slice(0, 3);
  const totalTopCompanies = topCompanies.reduce((s, c) => s + (c.total || 0), 0);
  const topSpecialties = (activeData.specialtyBreakdown || []).slice(0, 5);

  return (
    <div className="card flex flex-col gap-4" style={{ borderTop: `3px solid ${color}` }}>
      <div>
        <div className="flex items-center gap-2 mb-1">
          <div className="w-3 h-3 rounded-full shrink-0" style={{ background: color }} />
          <span className="font-display text-xl text-white">{list.list_name}</span>
        </div>
        <p className="text-white/30 text-xs mt-0.5 ml-5">
          {list.list_source === "filter" ? "🔍 Filter-built" : "📂 Uploaded"}
          {isAllYears && summary?.computedAt?.value && (
            <span className="ml-2">· Updated {new Date(summary.computedAt.value).toLocaleDateString()}</span>
          )}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="bg-navy-700/50 rounded-lg p-3 text-center">
          <div className="font-display text-2xl" style={{ color }}>{providersWithPayments.toLocaleString()}</div>
          <div className="text-white/30 text-xs mt-0.5">w/ payments</div>
        </div>
        <div className="bg-navy-700/50 rounded-lg p-3 text-center">
          <div className="font-display text-2xl text-white">{paymentRate}%</div>
          <div className="text-white/30 text-xs mt-0.5">of {totalListSize.toLocaleString()} providers</div>
        </div>
        <div className="bg-navy-700/50 rounded-lg p-3 text-center">
          <div className="font-display text-lg" style={{ color }}>{formatCompact(totalPayments)}</div>
          <div className="text-white/30 text-xs mt-0.5">{isAllYears ? "all years" : selectedYear}</div>
        </div>
        <div className="bg-navy-700/50 rounded-lg p-3 text-center">
          <div className="font-display text-lg text-white">{formatCompact(avgPerProvider)}</div>
          <div className="text-white/30 text-xs mt-0.5">avg per provider</div>
        </div>
      </div>

      {(summary || activeData) && (
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-red-500/5 border border-red-500/15 rounded-lg p-2 text-center">
            <div className="text-red-400 font-display text-lg">{isAllYears ? (summary?.exclusionCount ?? "—") : (activeData?.exclusionCount ?? "—")}</div>
            <div className="text-white/25 text-xs mt-0.5">excluded</div>
          </div>
          <div className="bg-amber-500/5 border border-amber-500/15 rounded-lg p-2 text-center">
            <div className="text-amber-400 font-display text-lg">{isAllYears ? (summary?.ownershipCount ?? "—") : (activeData?.ownershipCount ?? "—")}</div>
            <div className="text-white/25 text-xs mt-0.5">w/ ownership</div>
          </div>
          <div className="bg-teal-500/5 border border-teal-500/15 rounded-lg p-2 text-center">
            <div className="text-teal-400 font-display text-lg">{isAllYears ? (summary?.researchCount ?? "—") : (activeData?.researchCount ?? "—")}</div>
            <div className="text-white/25 text-xs mt-0.5">w/ research</div>
          </div>
        </div>
      )}

      {topCompanies.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-white/40 text-xs uppercase tracking-widest">Top companies</p>
            <button onClick={() => onExportCompanies(list)} className="print:hidden text-xs text-white/30 hover:text-white/60 transition-colors">↓ Export all</button>
          </div>
          <div className="bg-navy-700/50 rounded-lg p-3 space-y-3">
            {topCompanies.map((c, idx) => {
              const pct = totalTopCompanies > 0 ? ((c.total / totalTopCompanies) * 100).toFixed(1) : 0;
              return (
                <div key={c.companyName || idx}>
                  <div className="flex justify-between items-center mb-1">
                    {c.companyId ? (
                      <Link to={`/company/${c.companyId}`} className="text-white/80 text-xs font-medium truncate max-w-32 hover:text-gold-400 transition-colors block">{c.companyName || "Unknown"}</Link>
                    ) : (
                      <p className="text-white/80 text-xs font-medium truncate max-w-32">{c.companyName || "Unknown"}</p>
                    )}
                    <span className="text-xs shrink-0 ml-2" style={{ color }}>{formatCompact(c.total)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-white/5 rounded-full h-1.5">
                      <div className="h-1.5 rounded-full" style={{ width: `${pct}%`, background: color, opacity: 1 - idx * 0.2 }} />
                    </div>
                    <span className="text-white/30 text-xs shrink-0">{c.providerCount} providers</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {topSpecialties.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-white/40 text-xs uppercase tracking-widest">Top specialties</p>
            <button
              onClick={handleExportSpecialties}
              className="print:hidden text-xs text-white/30 hover:text-white/60 transition-colors"
            >
              ↓ Export all
            </button>
          </div>
          <div className="space-y-1">
            {topSpecialties.map((s) => {
              const parts = (s.specialty || "").split("|");
              const label = parts[parts.length - 1].trim() || s.specialty;
              return (
                <div key={s.specialty} className="flex justify-between items-center">
                  <span className="text-white/50 text-xs truncate max-w-36">{label}</span>
                  <span className="text-white/30 text-xs shrink-0 ml-2">{s.cnt} providers</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-white/40 text-xs uppercase tracking-widest">Top providers</p>
          <button onClick={() => onExportProviders(list)} className="print:hidden text-xs text-white/30 hover:text-white/60 transition-colors">↓ Export all</button>
        </div>
        {loadingProviders ? (
          <div className="text-white/30 text-xs animate-pulse">Loading providers…</div>
        ) : topProviders && topProviders.length > 0 ? (
          <div className="space-y-1.5">
            {topProviders.slice(0, 5).map((p) => (
              <div key={p.npi} className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <Link to={`/physician/${p.npi}?name=${encodeURIComponent(`${p.firstName} ${p.lastName}`)}&state=${p.state}`} className="text-xs text-white/70 hover:text-gold-400 transition-colors truncate max-w-28 block">
                      {p.firstName} {p.lastName}
                    </Link>
                    {p.hasDualInterest && <span className="text-amber-400 text-xs shrink-0" title="Dual interest">⚑</span>}
                    {p.isExcluded && <span className="text-red-400 text-xs shrink-0" title="Excluded">⚠</span>}
                  </div>
                  <p className="text-white/25 text-xs truncate max-w-28">
                    {p.specialty ? p.specialty.split("|").pop().trim() : "—"}
                  </p>
                </div>
                <span className="text-xs shrink-0" style={{ color }}>{formatCompact(p.totalPayments)}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-white/20 text-xs">No payment data for this period.</p>
        )}
      </div>
    </div>
  );
}

// ── Interest type categorizer (matches PhysicianOwnersPage logic) ─────────────
function categorize(terms) {
  if (!terms) return "Other";
  const t = terms.toUpperCase();
  if (t.includes("COMMON") && t.includes("PREFERRED")) return "Common & Preferred Stock";
  if (t.includes("RESTRICTED STOCK") || t.includes("RSU") || t.includes("PRSU")
    || t.includes("DEFERRED STOCK UNIT")) return "Restricted Stock / RSUs";
  if (t.includes("STOCK OPTION") || t.includes("OPTION TO PURCHASE")
    || t.includes("STOCK AND OR OPTIONS") || t.includes("STOCK AND/OR OPTIONS")
    || t.includes("OPTION GRANT") || t.includes("OPTIONS GRANTED")
    || t.includes("OPTIONS EXERCISED") || t.includes("EXERCISED OPTION")
    || t.includes("OPTIONS FOR SERVICES") || t.includes("OPTION FOR COMMON")
    || t.includes("NSO GRANT") || t.includes("VESTED OPTION")
    || t.includes("UNEXERCISED OPTION") || t.includes("BLACK-SCHOLES")
    || t.includes("BLACK SCHOLES") || t.includes("ADVISOR OPTION")
    || (t.includes("OPTION") && t.includes("EXERCISE"))) return "Stock Options";
  if (t.includes("WARRANT")) return "Warrants";
  if (t.includes("COMMON STOCK") || t.includes("COMMON SHARE")
    || t.includes("COMMON UNIT") || t.includes("ORDINARY SHARE")
    || t.includes("FOUNDER") || t === "COMMON"
    || t.includes("SHARES OF STOCK") || t.includes("COMPANY STOCK")
    || t.includes("STOCK OWNERSHIP") || t.includes("STOCK HOLDER")
    || t.includes("STOCKHOLDER") || t.includes("SHARE OWNERSHIP")
    || t.includes("STOCK GRANT") || t.includes("STOCK PURCHASE")
    || t.includes("PURCHASED CORPORATE SHARE") || t.includes("EQUITY STOCK")
    || t.includes("SHARES OF THE COMPANY") || t.includes("SHARES OF COMPANY")
    || t.includes("STOCK INTEREST")) return "Common Stock";
  if (t.includes("PREFERRED") || t.includes("SERIES A") || t.includes("SERIES B")
    || t.includes("SERIES C") || t.includes("SERIES D") || t.includes("SERIES E")
    || t.includes("SERIES F") || t.includes("SERIES SEED")) return "Preferred Stock";
  if (t.includes("LLC") || t.includes("L.L.C") || t.includes("MEMBERSHIP INTEREST")
    || t.includes("MEMBERSHIP UNIT") || t.includes("PROFIT INTEREST")
    || t.includes("PROFITS INTEREST") || t.includes("OPERATING AGREEMENT")
    || t.includes("CLASS A UNIT") || t.includes("CLASS B UNIT")
    || t.includes("CLASS C UNIT") || t.includes("CLASS D UNIT")
    || t.includes("B UNITS") || t.includes("A UNITS")
    || t.includes("UNIT HOLDER") || t.includes("UNITHOLDER")) return "LLC / Membership Interest";
  if (t.includes("CONVERTIBLE") || t.includes("SAFE INVESTMENT")
    || t.includes("SAFE 20%") || t.includes("PROMISSORY NOTE")
    || t === "SAFE") return "Convertible Instruments";
  if (t.includes("PARTNERSHIP") || t.includes("LP ") || t.includes("LLP")
    || t.includes("CAPITAL CONTRIBUTION") || t.includes("CAPITAL INVESTMENT")
    || t.includes("PARTNER CAPITAL") || t.includes("PARTNERSHIP SHARES")) return "Partnership Interest";
  if (t.includes("ACQUISITION") || t.includes("LIQUIDAT") || t.includes("REPURCHAS")
    || t.includes("BOUGHT BACK") || t.includes("BUYOUT") || t.includes("MERGER")
    || t.includes("COMPANY SOLD") || t.includes("ASSETS SOLD")
    || t.includes("CEASED BUSINESS") || t.includes("DISSOLVED")
    || t.includes("BANKRUPTCY") || t.includes("OUT OF BUSINESS")) return "Acquisition / Liquidation";
  if (t.includes("ROYALT") || t.includes("DIVIDEND") || t.includes("DISTRIBUTION")
    || t.includes("10% OF NET PROFIT") || t.includes("PROFIT BASED ON")
    || t.includes("20% RETURN") || t.includes("20% PROFIT")) return "Royalties / Distributions";
  if (t === "EQUITY" || t === "INVESTMENT" || t === "STOCK" || t === "SHARES"
    || t === "OWNERSHIP" || t === "INVESTOR" || t.includes("EQUITY INVESTMENT")
    || t.includes("EQUITY INTEREST") || t.includes("EQUITY OWNERSHIP")
    || t === "STOCK OWNERSHIP" || t === "PURCHASED SHARES"
    || t.includes("CASH INVESTMENT") || t.includes("PRIVATE PLACEMENT")
    || t.includes("OWNERSHIP INTEREST")) return "Equity Investment";
  return "Other";
}

// ── Ownership tab column ──────────────────────────────────────────────────────
function OwnershipColumn({ list, userId, color, loaded }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);

  async function handleExportOwnershipCompanies() {
    const companies = await getListAllOwnershipCompanies(userId, list.id);
    exportCsv(
      companies.map((c) => ({
        "Company ID": `'${c.companyId}`,
        "Company Name": c.companyName,
        "Holder Count": c.holderCount,
        "Active Holder Count": c.activeHolderCount,
        "Total Value": c.totalValue,
        "Active Value": c.activeValue,
        "List": list.list_name,
      })),
      `${list.list_name.replace(/\s+/g, "_")}_ownership_companies.csv`
    );
  }

  async function handleExportInterestTypes() {
    const detail = await getListOwnershipDetail(userId, list.id);
    const uniqueProviders = new Set(detail.map((r) => r.npi)).size;
    const rows = detail.map((r) => ({
      "NPI": r.npi,
      "First Name": r.firstName,
      "Last Name": r.lastName,
      "Specialty": r.specialty,
      "State": r.state,
      "Company ID": `'${r.companyId}`,
      "Company Name": r.companyName,
      "Interest Type (Grouped)": categorize(r.termsOfInterest),
      "Terms of Interest (Raw)": r.termsOfInterest,
      "Interest Holder": r.interestHolder,
      "Value of Interest": r.valueOfInterest,
      "Amount Invested": r.amountInvested,
      "Active Ownership": r.hasActiveOwnership ? "Yes" : "No",
      "First Year": r.firstYear,
      "Latest Year": r.latestYear,
      "Year Count": r.yearCount,
      "Dispute Status": r.disputeStatus,
      "Profile URL": `https://www.providerpaywatch.com/physician/${r.npi}?name=${encodeURIComponent(`${r.firstName} ${r.lastName}`)}&state=${r.state}`,
      "List": list.list_name,
    }));
    // Add summary note so users understand row count vs unique provider count
    rows.push({
      "NPI": `NOTE: ${detail.length} rows = one row per provider-company relationship. ${uniqueProviders} unique providers (some hold interests in multiple companies).`,
      "First Name": "", "Last Name": "", "Specialty": "", "State": "",
      "Company ID": "", "Company Name": "", "Interest Type (Grouped)": "",
      "Terms of Interest (Raw)": "", "Interest Holder": "", "Value of Interest": "",
      "Amount Invested": "", "Active Ownership": "", "First Year": "",
      "Latest Year": "", "Year Count": "", "Dispute Status": "", "Profile URL": "",
      "List": "",
    });
    exportCsv(rows, `${list.list_name.replace(/\s+/g, "_")}_ownership_interest_types.csv`);
  }

  useEffect(() => {
    if (!loaded || fetched) return;
    setLoading(true);
    setFetched(true);
    getListOwnershipSummary(userId, list.id)
      .then(setSummary)
      .catch(() => setSummary(null))
      .finally(() => setLoading(false));
  }, [loaded, list.id, userId, fetched]);

  if (!loaded) return null;
  if (loading) return (
    <div className="card flex flex-col gap-4" style={{ borderTop: `3px solid ${color}` }}>
      <div className="text-white/30 text-sm animate-pulse">Loading ownership data…</div>
    </div>
  );

  const topCompanies = summary?.topCompanies || [];
  const totalValue = summary?.activeValue || 0;

  return (
    <div className="card flex flex-col gap-4" style={{ borderTop: `3px solid ${color}` }}>
      <div className="flex items-center gap-2">
        <div className="w-3 h-3 rounded-full shrink-0" style={{ background: color }} />
        <span className="font-display text-xl text-white">{list.list_name}</span>
      </div>
      {!summary || summary.holderCount === 0 ? (
        <p className="text-white/30 text-sm">No ownership interest records found for this list.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-navy-700/50 rounded-lg p-3 text-center">
              <div className="font-display text-2xl" style={{ color }}>{summary.holderCount}</div>
              <div className="text-white/30 text-xs mt-0.5">holders</div>
            </div>
            <div className="bg-navy-700/50 rounded-lg p-3 text-center">
              <div className="text-green-400 font-display text-2xl">{summary.activeHolderCount}</div>
              <div className="text-white/30 text-xs mt-0.5">active</div>
            </div>
            <div className="bg-navy-700/50 rounded-lg p-3 text-center col-span-2">
              <div className="font-display text-lg" style={{ color }}>{formatCompact(summary.activeValue)}</div>
              <div className="text-white/30 text-xs mt-0.5">active value</div>
            </div>
          </div>
          {/* Physician vs family split */}
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-navy-700/50 rounded-lg p-3 text-center">
              <div className="font-display text-lg text-white">{summary.holderCount - summary.familyHolderCount}</div>
              <div className="text-white/30 text-xs mt-0.5">physician holders</div>
            </div>
            <div className="bg-navy-700/50 rounded-lg p-3 text-center">
              <div className="font-display text-lg text-violet-400">{summary.familyHolderCount}</div>
              <div className="text-white/30 text-xs mt-0.5">family member holders</div>
            </div>
          </div>

          {summary.familyHolderCount > 0 && (
            <div className="bg-violet-500/10 border border-violet-500/20 rounded-lg px-3 py-2 text-xs text-violet-300">
              ⚠ {summary.familyHolderCount} provider{summary.familyHolderCount !== 1 ? "s" : ""} with immediate family member holding interest — Stark Law extends to immediate family member ownership
            </div>
          )}

          {/* Interest type breakdown — grouped using same categorize() as PhysicianOwnersPage */}
          {summary.interestTypes?.length > 0 && (() => {
            const grouped = new Map();
            summary.interestTypes.forEach((t) => {
              const cat = categorize(t.termsOfInterest);
              if (!grouped.has(cat)) grouped.set(cat, { holderCount: 0, totalValue: 0 });
              const g = grouped.get(cat);
              g.holderCount += t.holderCount || 0;
              g.totalValue += t.totalValue || 0;
            });
            const sorted = [...grouped.entries()]
              .sort((a, b) => b[1].totalValue - a[1].totalValue)
              .filter(([cat]) => cat !== "Other");
            if (!sorted.length) return null;
            return (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-white/40 text-xs uppercase tracking-widest">Interest types</p>
                  <button
                    onClick={handleExportInterestTypes}
                  className="print:hidden text-xs text-white/30 hover:text-white/60 transition-colors"
                  >
                    ↓ Export detail
                  </button>
                </div>
                <div className="space-y-1.5">
                  {sorted.slice(0, 6).map(([cat, data]) => (
                    <div key={cat} className="flex justify-between items-center gap-2">
                      <span className="text-white/50 text-xs truncate min-w-0">{cat}</span>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-white/30 text-xs">{data.holderCount} holders</span>
                        <span className="text-xs" style={{ color }}>{formatCompact(data.totalValue)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {topCompanies.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-white/40 text-xs uppercase tracking-widest">Top companies by value</p>
                <button
                  onClick={handleExportOwnershipCompanies}
                  className="print:hidden text-xs text-white/30 hover:text-white/60 transition-colors"
                >
                  ↓ Export all
                </button>
              </div>
              <div className="bg-navy-700/50 rounded-lg p-3 space-y-3">
                {topCompanies.map((c, idx) => {
                  const pct = totalValue > 0 ? ((c.totalValue / totalValue) * 100).toFixed(1) : 0;
                  return (
                    <div key={c.companyName || idx}>
                      <div className="flex justify-between items-center mb-1 gap-2">
                        <p className="text-white/80 text-xs font-medium truncate min-w-0">{c.companyName || "Unknown"}</p>
                        <span className="text-xs shrink-0" style={{ color }}>{formatCompact(c.totalValue)}</span>
                      </div>
                      <div className="flex items-center gap-2 overflow-hidden">
                        <div className="flex-1 bg-white/5 rounded-full h-1.5 overflow-hidden min-w-0">
                          <div className="h-1.5 rounded-full" style={{ width: `${pct}%`, background: color, opacity: 1 - idx * 0.2 }} />
                        </div>
                        <span className="text-white/30 text-xs shrink-0">{c.holderCount} holders</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Research tab column ───────────────────────────────────────────────────────
function ResearchColumn({ list, userId, color, loaded }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);

  async function handleExportResearchCompanies() {
    const companies = await getListAllResearchCompanies(userId, list.id);
    exportCsv(
      companies.map((c) => ({
        "Company ID": `'${c.companyId}`,
        "Company Name": c.companyName,
        "Investigator Count": c.investigatorCount,
        "Active Investigators": c.activeCount,
        "Total Funding": c.totalFunding,
        "List": list.list_name,
      })),
      `${list.list_name.replace(/\s+/g, "_")}_research_companies.csv`
    );
  }

  useEffect(() => {
    if (!loaded || fetched) return;
    setLoading(true);
    setFetched(true);
    getListResearchSummary(userId, list.id)
      .then(setSummary)
      .catch(() => setSummary(null))
      .finally(() => setLoading(false));
  }, [loaded, list.id, userId, fetched]);

  if (!loaded) return null;
  if (loading) return (
    <div className="card flex flex-col gap-4" style={{ borderTop: `3px solid ${color}` }}>
      <div className="text-white/30 text-sm animate-pulse">Loading research data…</div>
    </div>
  );

  const topCompanies = summary?.topCompanies || [];
  const totalFunding = summary?.totalFunding || 0;
  const preclinicalPct = totalFunding > 0 ? ((summary.preclinicalFunding / totalFunding) * 100).toFixed(0) : 0;
  const clinicalPct = totalFunding > 0 ? ((summary.clinicalFunding / totalFunding) * 100).toFixed(0) : 0;

  return (
    <div className="card flex flex-col gap-4" style={{ borderTop: `3px solid ${color}` }}>
      <div className="flex items-center gap-2">
        <div className="w-3 h-3 rounded-full shrink-0" style={{ background: color }} />
        <span className="font-display text-xl text-white">{list.list_name}</span>
      </div>
      {!summary || summary.investigatorCount === 0 ? (
        <p className="text-white/30 text-sm">No research payment records found for this list.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-navy-700/50 rounded-lg p-3 text-center">
              <div className="font-display text-2xl" style={{ color }}>{summary.investigatorCount}</div>
              <div className="text-white/30 text-xs mt-0.5">investigators</div>
            </div>
            <div className="bg-navy-700/50 rounded-lg p-3 text-center">
              <div className="text-green-400 font-display text-2xl">{summary.activeCount}</div>
              <div className="text-white/30 text-xs mt-0.5">active ({DEFAULT_YEAR})</div>
            </div>
            <div className="bg-navy-700/50 rounded-lg p-3 text-center">
              <div className="font-display text-lg" style={{ color }}>{formatCompact(totalFunding)}</div>
              <div className="text-white/30 text-xs mt-0.5">total funding</div>
            </div>
            <div className="bg-navy-700/50 rounded-lg p-3 text-center">
              <div className="text-white font-display text-lg">{summary.companyCount}</div>
              <div className="text-white/30 text-xs mt-0.5">companies</div>
            </div>
          </div>
          {summary.preclinicalFunding > 0 && (
            <div>
              <p className="text-white/40 text-xs uppercase tracking-widest mb-2">Preclinical vs clinical</p>
              <div className="bg-navy-700/50 rounded-lg p-3 space-y-2">
                <div className="flex rounded-full overflow-hidden h-2">
                  <div className="bg-teal-500/70" style={{ width: `${clinicalPct}%` }} />
                  <div className="bg-violet-500/70" style={{ width: `${preclinicalPct}%` }} />
                </div>
                <div className="flex justify-between text-xs">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-sm bg-teal-500/70 shrink-0" />
                    <span className="text-white/50">Clinical {clinicalPct}%</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-sm bg-violet-500/70 shrink-0" />
                    <span className="text-violet-400">Preclinical {preclinicalPct}%</span>
                  </div>
                </div>
                <div className="flex justify-between text-xs pt-1 border-t border-white/5">
                  <span className="text-white/30">{formatCompact(summary.clinicalFunding)}</span>
                  <span className="text-violet-400/70">{formatCompact(summary.preclinicalFunding)}</span>
                </div>
              </div>
            </div>
          )}
          {topCompanies.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-white/40 text-xs uppercase tracking-widest">Top companies by funding</p>
                <button
                  onClick={handleExportResearchCompanies}
                  className="print:hidden text-xs text-white/30 hover:text-white/60 transition-colors"
                >
                  ↓ Export all
                </button>
              </div>
              <div className="bg-navy-700/50 rounded-lg p-3 space-y-3">
                {topCompanies.map((c, idx) => {
                  const pct = totalFunding > 0 ? ((c.totalFunding / totalFunding) * 100).toFixed(1) : 0;
                  return (
                    <div key={c.companyName || idx}>
                      <div className="flex justify-between items-center mb-1">
                        <p className="text-white/80 text-xs font-medium truncate max-w-32">{c.companyName || "Unknown"}</p>
                        <span className="text-xs shrink-0 ml-2" style={{ color }}>{formatCompact(c.totalFunding)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-white/5 rounded-full h-1.5">
                          <div className="h-1.5 rounded-full" style={{ width: `${pct}%`, background: color, opacity: 1 - idx * 0.2 }} />
                        </div>
                        <span className="text-white/30 text-xs shrink-0">{c.investigatorCount} investigators</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function CompareListsPage() {
  const [searchParams] = useSearchParams();
  const { user, isSignedIn } = useUser();
  const { lists } = useMyProviders();
  const [activeTab, setActiveTab] = useState("general");
  const [loadedTabs, setLoadedTabs] = useState({ general: true, ownership: false, research: false });
  const [summaries, setSummaries] = useState({});
  const [yearData, setYearData] = useState({});
  const [topProviders, setTopProviders] = useState({});
  const [loadingProviders, setLoadingProviders] = useState({});
  const [selectedYear, setSelectedYear] = useState("all");
  const [copied, setCopied] = useState(false);

  // Trend data — per tab, per list
  const [generalTrend, setGeneralTrend] = useState({});
  const [ownershipTrend, setOwnershipTrend] = useState({});
  const [researchTrend, setResearchTrend] = useState({});

  // Selected companies — independent per tab
  const [generalCompanies, setGeneralCompanies] = useState([]);
  const [ownershipCompanies, setOwnershipCompanies] = useState([]);
  const [researchCompanies, setResearchCompanies] = useState([]);

  const listIdParam = searchParams.get("listIds") || "";
  const listIds = listIdParam.split(",").filter(Boolean);
  const selectedLists = listIds.map((id) => lists.find((l) => l.id === id)).filter(Boolean);

  // Derive union of top companies across all lists per tab for dropdown
  function getUnionCompanies(trendData) {
    const map = new Map();
    selectedLists.forEach((l) => {
      (trendData[l.id] || []).forEach((c) => {
        if (!map.has(c.companyName)) map.set(c.companyName, { companyName: c.companyName, grandTotal: 0 });
        map.get(c.companyName).grandTotal += c.grandTotal;
      });
    });
    return [...map.values()].sort((a, b) => b.grandTotal - a.grandTotal).slice(0, 50);
  }

  const generalUnionCompanies = getUnionCompanies(generalTrend);
  const ownershipUnionCompanies = getUnionCompanies(ownershipTrend);
  const researchUnionCompanies = getUnionCompanies(researchTrend);

  // Load summaries + fallback on mount
  useEffect(() => {
    if (!user || selectedLists.length === 0) return;
    let cancelled = false;

    async function loadAllData() {
      const summaryResults = await Promise.all(
        selectedLists.map((list) => getListSummary(user.id, list.id).catch(() => null))
      );
      if (cancelled) return;

      const needsFallback = [];
      summaryResults.forEach((summary, i) => {
        const list = selectedLists[i];
        if (summary) setSummaries((prev) => ({ ...prev, [list.id]: summary }));
        else needsFallback.push(list);
      });

      if (needsFallback.length > 0) {
        const fallbackResults = await Promise.all(
          needsFallback.map((list) => getListGeneralByYear(user.id, list.id, "all").catch(() => null))
        );
        if (cancelled) return;
        fallbackResults.forEach((data, i) => {
          if (data) setYearData((prev) => ({ ...prev, [needsFallback[i].id]: data }));
        });
      }

      // Load general trend data for all lists
      if (!cancelled) {
        const trendResults = await Promise.all(
          selectedLists.map((list) => getListCompanyTrend(user.id, list.id).catch(() => []))
        );
        if (cancelled) return;
        const trendMap = {};
        trendResults.forEach((data, i) => { trendMap[selectedLists[i].id] = data || []; });
        setGeneralTrend(trendMap);
        // Pre-select top company
        setGeneralCompanies([ALL_COMPANIES_KEY]);
      }
    }

    loadAllData();
    return () => { cancelled = true; };
  }, [user?.id, listIdParam]);

  function getTopCompanyAcrossLists(trendResults, lists) {
    const map = new Map();
    trendResults.forEach((data) => {
      (data || []).forEach((c) => {
        map.set(c.companyName, (map.get(c.companyName) || 0) + c.grandTotal);
      });
    });
    if (!map.size) return null;
    return [...map.entries()].sort((a, b) => b[1] - a[1])[0][0];
  }

  // Load ownership trend when ownership tab first opened
  useEffect(() => {
    if (!loadedTabs.ownership || !user || selectedLists.length === 0) return;
    if (Object.keys(ownershipTrend).length > 0) return;
    let cancelled = false;

    Promise.all(
      selectedLists.map((list) => getListOwnershipTrend(user.id, list.id).catch(() => []))
    ).then((results) => {
      if (cancelled) return;
      const map = {};
      results.forEach((data, i) => { map[selectedLists[i].id] = data || []; });
      setOwnershipTrend(map);
      setOwnershipCompanies([ALL_COMPANIES_KEY]);
    });

    return () => { cancelled = true; };
  }, [loadedTabs.ownership, user?.id, listIdParam]);

  // Load research trend when research tab first opened
  useEffect(() => {
    if (!loadedTabs.research || !user || selectedLists.length === 0) return;
    if (Object.keys(researchTrend).length > 0) return;
    let cancelled = false;

    Promise.all(
      selectedLists.map((list) => getListResearchTrend(user.id, list.id).catch(() => []))
    ).then((results) => {
      if (cancelled) return;
      const map = {};
      results.forEach((data, i) => { map[selectedLists[i].id] = data || []; });
      setResearchTrend(map);
      setResearchCompanies([ALL_COMPANIES_KEY]);
    });

    return () => { cancelled = true; };
  }, [loadedTabs.research, user?.id, listIdParam]);

  // Re-fetch year data when selectedYear changes
  useEffect(() => {
    if (!user || selectedLists.length === 0) return;
    let cancelled = false;

    if (selectedYear === "all") {
      setYearData({});
      selectedLists.forEach((list) => {
        if (summaries[list.id]) return;
        getListGeneralByYear(user.id, list.id, "all")
          .then((data) => { if (!cancelled && data) setYearData((prev) => ({ ...prev, [list.id]: data })); })
          .catch(() => {});
      });
      return () => { cancelled = true; };
    }

    selectedLists.forEach((list) => {
      getListGeneralByYear(user.id, list.id, selectedYear)
        .then((data) => { if (!cancelled && data) setYearData((prev) => ({ ...prev, [list.id]: data })); })
        .catch(() => {});
    });

    return () => { cancelled = true; };
  }, [user?.id, listIdParam, selectedYear]);

  // Fetch top providers whenever year changes
  useEffect(() => {
    if (!user || selectedLists.length === 0) return;
    selectedLists.forEach((list) => {
      setLoadingProviders((prev) => ({ ...prev, [list.id]: true }));
      getListTopProviders(user.id, list.id, selectedYear === "all" ? null : selectedYear)
        .then((providers) => setTopProviders((prev) => ({ ...prev, [list.id]: providers || [] })))
        .catch(() => setTopProviders((prev) => ({ ...prev, [list.id]: [] })))
        .finally(() => setLoadingProviders((prev) => ({ ...prev, [list.id]: false })));
    });
  }, [user?.id, listIdParam, selectedYear]);

  async function handleExportCompanies(list) {
    const companies = await getListAllCompanies(user.id, list.id, selectedYear === "all" ? null : selectedYear);
    const yearLabel = selectedYear === "all" ? "all_years" : selectedYear;
    exportCsv(
      companies.map((c) => ({
        "Company ID": `'${c.companyId || ""}`,
        "Company Name": c.companyName,
        "Total Payments": c.totalPayments,
        "Provider Count": c.providerCount,
        "Payment Count": c.paymentCount,
        "Year": yearLabel,
        "List": list.list_name,
      })),
      `${list.list_name.replace(/\s+/g, "_")}_companies_${yearLabel}.csv`
    );
  }

  async function handleExportProviders(list) {
    const providers = topProviders[list.id] || [];
    const yearLabel = selectedYear === "all" ? "all_years" : selectedYear;
    exportCsv(
      providers.map((p) => ({
        "NPI": p.npi,
        "First Name": p.firstName,
        "Last Name": p.lastName,
        "Specialty": p.specialty,
        "State": p.state,
        "Total Payments": p.totalPayments,
        "Payment Count": p.paymentCount,
        "Has Dual Interest": p.hasDualInterest ? "Yes" : "No",
        "Is Excluded": p.isExcluded ? "Yes" : "No",
        "Profile URL": `https://www.providerpaywatch.com/physician/${p.npi}?name=${encodeURIComponent(`${p.firstName} ${p.lastName}`)}&state=${p.state}`,
        "Year": yearLabel,
        "List": list.list_name,
      })),
      `${list.list_name.replace(/\s+/g, "_")}_providers_${yearLabel}.csv`
    );
  }

  function handleTabClick(tabId) {
    setActiveTab(tabId);
    setLoadedTabs((prev) => ({ ...prev, [tabId]: true }));
  }

  function shareComparison() {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  if (!isSignedIn) return (
    <main className="max-w-5xl mx-auto px-4 py-10">
      <p className="text-white/40 text-sm">Sign in to compare provider lists.</p>
    </main>
  );

  if (selectedLists.length === 0) return (
    <main className="max-w-5xl mx-auto px-4 py-10">
      <p className="text-white/40 text-sm">No lists found. Lists may still be loading.</p>
      <Link to="/my-providers" className="text-gold-400 text-sm mt-2 block">← My Providers</Link>
    </main>
  );

  if (selectedLists.length < 2) return (
    <main className="max-w-5xl mx-auto px-4 py-10">
      <p className="text-white/40 text-sm">Select 2 or 3 lists from My Providers to compare.</p>
      <Link to="/my-providers" className="text-gold-400 text-sm mt-2 block">← My Providers</Link>
    </main>
  );

  return (
    <main className="max-w-5xl mx-auto px-4 py-10">
      {/* ── Print styles ──────────────────────────────────────────────────── */}
      <style>{`
        @media print {
          @page { margin: 0.75in; size: ${selectedLists.length === 3 ? "letter landscape" : "letter"}; }
          body {
            background: white !important;
            color: #111 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          nav, header, footer, .print-hide { display: none !important; }
          main { background: white !important; max-width: 100% !important; padding: 0 !important; }
          .card {
            background: white !important;
            border: 1px solid #e5e7eb !important;
            border-radius: 8px !important;
            box-shadow: none !important;
            break-inside: avoid;
          }
          .text-white { color: #111 !important; }
          .text-white\\/80, .text-white\\/70, .text-white\\/60, .text-white\\/50 { color: #374151 !important; }
          .text-white\\/40, .text-white\\/30, .text-white\\/25, .text-white\\/20 { color: #6b7280 !important; }
          .text-gold-400 { color: #b45309 !important; }
          .text-amber-400 { color: #b45309 !important; }
          .text-teal-400 { color: #0f766e !important; }
          .text-red-400 { color: #dc2626 !important; }
          .text-orange-400 { color: #ea580c !important; }
          .text-green-400 { color: #16a34a !important; }
          .text-blue-400 { color: #2563eb !important; }
          .text-violet-400 { color: #7c3aed !important; }
          svg text { fill: #374151 !important; }
          polyline[stroke] { stroke-opacity: 1 !important; }
          circle[fill] { fill-opacity: 1 !important; }

          /* Force bar/line colors */
          div[style] { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          div[style*="background-color: rgba(251,191,36"] { background-color: #d97706 !important; opacity: 1 !important; }
          div[style*="background-color: rgba(139,92,246"] { background-color: #7c3aed !important; opacity: 1 !important; }
          div[style*="background-color: rgba(20,184,166"] { background-color: #0f766e !important; opacity: 1 !important; }
          div[style*="background-color: rgba(34,197,94"]  { background-color: #16a34a !important; opacity: 1 !important; }
          div[style*="background-color: rgba(59,130,246"] { background-color: #2563eb !important; opacity: 1 !important; }
          div[style*="background-color: rgba(239,68,68"]  { background-color: #dc2626 !important; opacity: 1 !important; }
          div[style*="background-color: rgba(249,115,22"] { background-color: #ea580c !important; opacity: 1 !important; }
          div[style*="rgba(255,255,255,0.04"] { display: none !important; }
          div[style*="rgba(255,255,255,0.05"] { display: none !important; }

          /* Banner and stat backgrounds */
          .bg-red-500\\/5 { background: #fef2f2 !important; border-color: #fca5a5 !important; }
          .bg-amber-500\\/5 { background: #fffbeb !important; border-color: #fcd34d !important; }
          .bg-teal-500\\/5 { background: #f0fdfa !important; border-color: #5eead4 !important; }
          .bg-violet-500\\/10 { background: #f5f3ff !important; border-color: #c4b5fd !important; }
          .bg-navy-700\\/50 { background: #f9fafb !important; border: 1px solid #e5e7eb !important; }

          /* Column grid */
          .compare-grid {
            display: grid !important;
            grid-template-columns: repeat(${selectedLists.length}, 1fr) !important;
            gap: 12px !important;
          }

          /* Hide interactive controls */
          select { display: none !important; }
          button { display: none !important; }
        }
      `}</style>

      {/* ── Print-only report header ───────────────────────────────────────── */}
      <div className="hidden print:block mb-6 pb-4 border-b border-gray-200">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-gray-400 uppercase tracking-widest mb-1">
              ProviderPayWatch · List Comparison Report
            </p>
            <h1 className="text-2xl font-bold text-gray-900">
              {selectedLists.map((l) => l.list_name).join(" vs. ")}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {selectedLists.length} provider lists · {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} tab
              {selectedYear !== "all" ? ` · ${selectedYear}` : " · All years"}
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-400">Generated {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</p>
            <p className="text-xs text-gray-400 mt-0.5">providerpaywatch.com</p>
          </div>
        </div>
        <p className="text-xs text-gray-400 mt-3">
          Source: CMS Open Payments (2016–2025) · Data reported by applicable manufacturers and GPOs as required by the Physician Payments Sunshine Act · This report supports compliance research and risk review — consult qualified legal or compliance counsel for final determinations.
        </p>
      </div>

      {/* Header */}
      <div className="print:hidden flex items-center justify-between mb-6">
        <div>
          <Link to="/my-providers" className="text-white/40 hover:text-white/70 text-sm transition-colors">← My Providers</Link>
          <h1 className="font-display text-3xl text-white mt-2">List <span className="text-gold-400">comparison</span></h1>
          <p className="text-white/40 text-sm mt-1">Comparing {selectedLists.length} provider lists</p>
        </div>
        <div className="flex items-center gap-3">
          <YearDropdown selectedYear={selectedYear} onChange={setSelectedYear} />
          <button
            onClick={shareComparison}
            className={`flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border transition-all ${copied ? "text-green-400 border-green-500/30 bg-green-500/10" : "text-white/60 border-white/20 hover:text-white hover:border-white/40 bg-white/5"}`}
          >
            {copied ? "✓ Link copied!" : "⎘ Share comparison"}
          </button>
          <button
            onClick={() => window.print()}
            className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border border-white/20 text-white/60 hover:text-white hover:border-white/40 bg-white/5 transition-all"
          >
            <span>⎙</span><span>Print / Save PDF</span>
          </button>
        </div>
      </div>

      {/* Payment trend bar chart */}
      <PaymentTrendChart lists={selectedLists} summaries={summaries} yearData={yearData} selectedYear={selectedYear} />

      {/* Tab bar */}
      <div className="print:hidden flex gap-1 mb-5 border-b border-white/10">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => handleTabClick(tab.id)}
            className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${activeTab === tab.id ? "text-gold-400 border-gold-400" : "text-white/40 border-transparent hover:text-white/70"}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* General tab */}
      {activeTab === "general" && (
        <>
          <p className="print:hidden text-white/20 text-xs mb-4">
            {selectedYear === "all" ? "Showing all years · 2016–2025" : `Showing ${selectedYear} data`}
          </p>

          {/* Company trend line chart */}
          {generalUnionCompanies.length > 0 && (
            <div className="mb-6">
              <div className="print:hidden flex items-center gap-3 mb-3">
                <h3 className="text-white/40 text-xs uppercase tracking-widest">Company payment trend</h3>
                <CompanyDropdown
                  companies={generalUnionCompanies}
                  selected={generalCompanies}
                  onChange={setGeneralCompanies}
                  showAllOption={true}
                />
              </div>
              {generalCompanies.length > 0 && (
                <CompanyTrendChart
                  lists={selectedLists}
                  trendData={generalTrend}
                  selectedCompanies={generalCompanies}
                  selectedYear={selectedYear}
                  metric="Total Payments"
                  title="Company payment trend — all years"
                />
              )}
            </div>
          )}

          <div className="print:overflow-visible overflow-x-auto -mx-4 px-4">
            <div className="grid gap-4 compare-grid" style={{ gridTemplateColumns: `repeat(${selectedLists.length}, minmax(280px, 1fr))`, minWidth: selectedLists.length > 1 ? `${selectedLists.length * 290}px` : "auto" }}>
              {selectedLists.map((list, i) => (
                <GeneralColumn
                  key={`${list.id}-${selectedYear}`}
                  list={list}
                  summary={summaries[list.id]}
                  yearData={yearData[list.id]}
                  topProviders={topProviders[list.id]}
                  loadingProviders={loadingProviders[list.id]}
                  color={CHART_COLORS[i]}
                  selectedYear={selectedYear}
                  onExportCompanies={handleExportCompanies}
                  onExportProviders={handleExportProviders}
                />
              ))}
            </div>
          </div>
        </>
      )}

      {/* Ownership tab */}
      {activeTab === "ownership" && (
        <>
          <p className="print:hidden text-white/20 text-xs mb-4">Ownership & investment interest data from CMS Open Payments</p>

          {/* Ownership trend line chart */}
          {ownershipUnionCompanies.length > 0 && (
            <div className="mb-6">
              <div className="print:hidden flex items-center gap-3 mb-3">
                <h3 className="text-white/40 text-xs uppercase tracking-widest">Ownership value trend</h3>
                <CompanyDropdown
                  companies={ownershipUnionCompanies}
                  selected={ownershipCompanies}
                  onChange={setOwnershipCompanies}
                  showAllOption={true}
                />
              </div>
              {ownershipCompanies.length > 0 && (
                <CompanyTrendChart
                  lists={selectedLists}
                  trendData={ownershipTrend}
                  selectedCompanies={ownershipCompanies}
                  selectedYear={selectedYear}
                  metric="Ownership Value"
                  title="Ownership value trend — all years"
                />
              )}
            </div>
          )}

          <div className="print:overflow-visible overflow-x-auto -mx-4 px-4">
            <div className="grid gap-4 compare-grid" style={{ gridTemplateColumns: `repeat(${selectedLists.length}, minmax(280px, 1fr))`, minWidth: selectedLists.length > 1 ? `${selectedLists.length * 290}px` : "auto" }}>
              {selectedLists.map((list, i) => (
                <OwnershipColumn key={list.id} list={list} userId={user?.id} color={CHART_COLORS[i]} loaded={loadedTabs.ownership} />
              ))}
            </div>
          </div>
        </>
      )}

      {/* Research tab */}
      {activeTab === "research" && (
        <>
          <p className="print:hidden text-white/20 text-xs mb-4">Research payment data from CMS Open Payments</p>

          {/* Research trend line chart */}
          {researchUnionCompanies.length > 0 && (
            <div className="mb-6">
              <div className="print:hidden flex items-center gap-3 mb-3">
                <h3 className="text-white/40 text-xs uppercase tracking-widest">Research funding trend</h3>
                <CompanyDropdown
                  companies={researchUnionCompanies}
                  selected={researchCompanies}
                  onChange={setResearchCompanies}
                  showAllOption={true}
                />
              </div>
              {researchCompanies.length > 0 && (
                <CompanyTrendChart
                  lists={selectedLists}
                  trendData={researchTrend}
                  selectedCompanies={researchCompanies}
                  selectedYear={selectedYear}
                  metric="Research Funding"
                  title="Research funding trend — all years"
                />
              )}
            </div>
          )}

          <div className="print:overflow-visible overflow-x-auto -mx-4 px-4">
            <div className="grid gap-4 compare-grid" style={{ gridTemplateColumns: `repeat(${selectedLists.length}, minmax(280px, 1fr))`, minWidth: selectedLists.length > 1 ? `${selectedLists.length * 290}px` : "auto" }}>
              {selectedLists.map((list, i) => (
                <ResearchColumn key={list.id} list={list} userId={user?.id} color={CHART_COLORS[i]} loaded={loadedTabs.research} />
              ))}
            </div>
          </div>
        </>
      )}

      <p className="print:hidden text-white/20 text-xs text-center mt-8">
        Source: CMS Open Payments · Data reported by manufacturers under the Sunshine Act
      </p>
      <p className="hidden print:block text-gray-400 text-xs text-center mt-8">
        Source: CMS Open Payments (2016–2025) · providerpaywatch.com · Generated {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
      </p>
    </main>
  );
}